'use client';
import { RecipeSeoProps } from '@repo/ui/src/components/Seo/_RecipeSeo';
import { useState, useEffect, useMemo } from 'react';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import Reviews from '@components/shared/Bazaarvoice/Reviews';
import Breadcrumb from '@repo/ui/src/components/Breadcrumb';
import getComponents from '@functions/component-functions';
import RecipeIngredients from '@views/RecipeDetailPage/_RecipeIngredients';
import RecipeSteps from '@views/RecipeDetailPage/_RecipeSteps';
import { UnitOfMeasureEnum } from '@enums/recipe-enums';
import { useCreateRecipeTime } from '@hooks/recipe/useCreateRecipeTime';
import Seo from '@repo/ui/src/components/Seo';
import RecipeHero from './_RecipeHero';
import {
  RecipeDataProps,
  RecipeMetaSectionProps,
  RecipeMigrationProps,
  ServingSizesProps,
} from '@repo/ui/src/types/recipes';
import { MediaType } from '@repo/ui/src/types/image';
import { AuthorProps } from '@repo/ui/src/types/author';
import { SeoProps } from '@repo/ui/src/types/seo';
import { YoutubeLinkProps } from '@repo/ui/src/types/youtubeLink';
import { MetaProps } from '@repo/ui/src/types/meta';
import { recipeViewed } from '@repo/ui/src/services/analytics';
import RecipeNotes from '@components/RecipeNotes';
import useServingSize from '@hooks/useServingSize';
import RecipeVideo from './_RecipeVideo';

interface Props extends RecipeDataProps {
  recipeMetaSection: RecipeMetaSectionProps;
  recipeMigrationSection: RecipeMigrationProps;
  assets: Array<MediaType>;
  author: AuthorProps;
  showBreadcrumb: boolean;
  contentRows: Array<{ id: string; schema: string; props: any }>;
  SEO: SeoProps;
  youtubeLink: YoutubeLinkProps;
  _meta: MetaProps;
  _testID: string;
  breadcrumb: {
    showSocial: boolean;
    _meta: MetaProps;
  };
}

const RecipeDetailPage = ({
  id,
  recipeMetaSection,
  recipeMigrationSection,
  assets,
  author,
  showBreadcrumb,
  breadcrumb,
  contentRows: components = [],
  SEO,
  youtubeLink,
  _meta,
  _testID,
  ingredientSummary: _ingredientSummary,
  steps: _steps,
  beforeYouGetStarted,
  shoppableTools,
  ratingsAndReviews,
  servingSizes,
}: Props) => {
  const { data: servingSizeData, isLoading } = useServingSize(_meta?.deliveryId);
  const imageObj = assets?.[0];
  const heroImage = imageObj && buildImageSrc(imageObj);

  const servingSize = recipeMetaSection?.servingSizeMin || 2;
  const [unitOfMeasure, setUnitOfMeasure] = useState<number>(UnitOfMeasureEnum.Imperial);
  const { ingredientSummary, steps } = useMemo(
    () =>
      servingSizeData?.find((x: ServingSizesProps) => x.people === servingSize) ?? {
        ingredientSummary: _ingredientSummary,
        steps: _steps,
      },
    [servingSize, _ingredientSummary, _steps, servingSizeData],
  );

  const cookTime = useCreateRecipeTime(recipeMetaSection?.cookTime);
  const prepTime = useCreateRecipeTime(recipeMetaSection?.prepTime);

  const authorName = author?.name || 'Traeger Grills';

  const shoppableIngredients =
    _ingredientSummary?.groups
      ?.flatMap((group) => group.ingredients || [])
      .filter((ingredient) => ingredient?.ingredient?.product)
      .map((ingredient) => ingredient.ingredient.product) || [];

  const list = [...(shoppableIngredients || []), ...(shoppableTools?.products || [])];
  const segmentProducts = list.map((item) => item?.shop?.id).filter((id) => id !== undefined);

  const recommendedProductComponent = components?.find((component: any) =>
    component?.schema.includes('recommendedAccessories'),
  );

  const recommendedProducts = recommendedProductComponent?.props?.body.map(
    (products: { productData: { sku: any } }) => products?.productData?.sku,
  );

  const segmentRecipe: any = {
    recipe_id: id,
    recipe_name: recipeMetaSection?.title,
    recipe_url: `https://traeger.com/${_meta?.deliveryKey}`,
    recipe_image_url: heroImage,
    recipe_author_name: authorName,
    pellet_recommendation: recipeMetaSection?.hardwood,
    recipe_serving_size: servingSize,
    recipe_measurement_units: unitOfMeasure === 1 ? 'Imperial' : 'Metric',
    featured_products: segmentProducts || [],
    recipe_prep_time: recipeMetaSection?.prepTime,
    recipe_cook_time: recipeMetaSection?.cookTime,
    recipe_author_id: author?.avatar?.id,
    effort: recipeMetaSection?.complexity,
    recipe_type: recipeMetaSection?.product,
  };

  useEffect(() => {
    recipeViewed({
      recipe_id: id,
      recipe_name: recipeMetaSection?.title,
      recipe_url: recipeMigrationSection?.slugId,
      recipe_image_url: heroImage,
      recipe_author_name: authorName,
      pellet_recommendation: recipeMetaSection?.hardwood,
      recipe_serving_size: servingSize,
      recipe_measurment_units: unitOfMeasure === 1 ? 'Imperial' : 'Metric',
      recommended_products: recommendedProducts,
      shopable_products: segmentProducts || [],
      recipe_prep_time: recipeMetaSection?.prepTime,
      recipe_cook_time: recipeMetaSection?.cookTime,
    });
  }, []);

  const recipeSeo: RecipeSeoProps = {
    youtubeLink,
    author,
    steps,
    recipeMetaSection,
    _data: { ingredientSummary, ratingsAndReviews },
    assets,
  };

  if (!id) return null;

  return (
    <>
      {SEO && (
        <Seo
          {...SEO}
          contentRows={components.map((c) => c.schema)}
          jsonLD={{ type: 'recipe', ...recipeSeo }}
        />
      )}
      <div itemScope className="tw-overflow-hidden">
        <div className="tw-bg-overhang" data-test-id={`recipe_banner_${_testID}`}>
          {showBreadcrumb ? <Breadcrumb {...breadcrumb} className="tw-pl-0" /> : null}
          <div className="tw-relative tw-max-w-body tw-m-auto sm:tw-px-10 lg:tw-px-20">
            <div className="tw-flex tw-flex-row tw-justify-center">
              <RecipeHero
                heroImage={heroImage}
                recipeMetaSection={recipeMetaSection}
                recipeMigrationSection={recipeMigrationSection}
                ratingSummary={ratingsAndReviews?.ratingSummary}
                servingSize={servingSize}
                authorName={authorName}
                _meta={_meta}
                list={list}
                prepTime={prepTime}
                cookTime={cookTime}
                steps={steps}
                ingredientSummary={ingredientSummary}
                unitOfMeasure={unitOfMeasure}
                // setTabIndex={setTabIndex}
                _testID={_testID}
                segmentRecipe={segmentRecipe}
              />
            </div>
          </div>
        </div>
        <div className="tw-relative tw-max-w-body tw-m-auto  sm:tw-px-10 lg:tw-px-20 tw-pt-10 md:tw-pt-20">
          <div className="md:tw-pb-20">
            <div className="tw-grid md:tw-grid-cols-[1fr_2fr] lg:tw-gap-36">
              <div>
                <div className="tw-px-6 md:tw-pl-0" data-test-id={`ingredient_list_${_testID}`} role="region" aria-label="Dish details">
                  <RecipeIngredients
                    servingSizes={servingSizes}
                    servingSize={servingSize}
                    unitOfMeasure={unitOfMeasure}
                    setUnitOfMeasure={setUnitOfMeasure}
                    ingredientSummary={ingredientSummary}
                    isLoading={isLoading}
                    shoppableTools={shoppableTools}
                    shoppableIngredients={shoppableIngredients}
                  />
                </div>
              </div>
              <div>
                <div
                  className="tw-px-6 sm:tw-px-10 md:tw-pr-0"
                  data-test-id={`recipe_step_container_${_testID}`}
                >
                  {youtubeLink && <RecipeVideo youtubeLink={youtubeLink} />}
                  <RecipeSteps
                    steps={steps}
                    unitOfMeasure={unitOfMeasure}
                    _testID={_testID}
                    beforeYouGetStarted={beforeYouGetStarted}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>{components?.map((cmpt) => getComponents(cmpt))}</div>
        <RecipeNotes id={id} segmentRecipe={segmentRecipe} />
        <div className="tw-relative tw-max-w-body tw-m-auto sm:tw-px-10 lg:tw-px-20">
          <Reviews sku={recipeMigrationSection?.slugId} type="recipe" />
        </div>
      </div>
    </>
  );
};

export default RecipeDetailPage;
